import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  structuredData?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  structuredData,
}: SEOProps) => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://okayama-pet-funeral.example.com';
    
    // Update title
    document.title = title;
    
    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Primary meta tags
    updateMetaTag('title', title);
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical || siteUrl + window.location.pathname;

    // Open Graph tags
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', canonical || siteUrl + window.location.pathname, true);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);

    // Twitter tags
    updateMetaTag('twitter:url', canonical || siteUrl + window.location.pathname, true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);

    // Last modified
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // Structured Data
    if (structuredData) {
      let script = document.querySelector('script[data-seo-structured-data]');
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-seo-structured-data', 'true');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }

    return () => {
      // Cleanup structured data on unmount
      const script = document.querySelector('script[data-seo-structured-data]');
      if (script) {
        script.remove();
      }
    };
  }, [title, description, keywords, canonical, ogType, structuredData]);
};
